import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, X, ChevronDown, ChevronUp } from "lucide-react";
import { fetchWithAuth } from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * Claire Walker meeting transcripts for one account.
 *
 * Distinct from the "Meetings" section on this page — that one is the Zoom
 * pipeline and owns its own transcript_text. This is a separate corpus of
 * Otter/Recall meetings, matched to accounts by an LLM sweep and confirmed by a
 * human here. Only confirmed transcripts reach the AI chat context, so an
 * unreviewed match can never become an answer the assistant states as fact.
 */

export interface CorpusLink {
  id: string;
  transcript_id: string;
  entity_label: string | null;
  confidence: number | null;
  status: string;
  transcript: {
    title: string | null;
    created_at: string;
    source_type: string;
    tags: string[];
    summary: string | null;
  } | null;
}

function useCorpusLinks(status: string, accountId: string) {
  return useQuery({
    queryKey: ["corpus-links", status, accountId],
    queryFn: () =>
      fetchWithAuth<{ links: CorpusLink[] }>(
        `/api/corpus/links?status=${status}&account_id=${accountId}`,
      ).then((r) => r.links),
    // 503 when CORPUS_* is unset — don't hammer it.
    retry: false,
  });
}

function LinkRow({ link, reviewable }: { link: CorpusLink; reviewable: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const qc = useQueryClient();
  const review = useMutation({
    mutationFn: (status: "confirmed" | "rejected") =>
      fetchWithAuth(`/api/corpus/links/${link.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["corpus-links"] }),
  });

  const t = link.transcript;
  return (
    <div className="text-xs border rounded-md p-2 space-y-1">
      <div className="flex items-start justify-between gap-1">
        <span className="font-medium flex-1">{t?.title ?? "(untitled)"}</span>
        {reviewable && (
          <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800">
            {((link.confidence ?? 0) * 100).toFixed(0)}%
          </span>
        )}
      </div>
      <div className="text-muted-foreground">{t?.created_at?.slice(0, 10)}</div>
      {expanded && <p className="text-muted-foreground whitespace-pre-wrap pt-1">{t?.summary ?? "No summary."}</p>}
      <div className="flex items-center gap-2 pt-0.5">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"
        >
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? "Hide" : "Summary"}
        </button>
        {reviewable && (
          <>
            <Button
              size="sm"
              variant="ghost"
              className="h-5 px-1.5 text-[11px] text-emerald-700"
              disabled={review.isPending}
              onClick={() => review.mutate("confirmed")}
            >
              <Check className="w-3 h-3 mr-0.5" /> Confirm
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-5 px-1.5 text-[11px] text-rose-700"
              disabled={review.isPending}
              onClick={() => review.mutate("rejected")}
            >
              <X className="w-3 h-3 mr-0.5" /> Reject
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

interface CorpusTranscript {
  id: string;
  title: string | null;
  created_at: string;
  summary: string | null;
}

/**
 * What this account actually has available. Goes through the server's
 * resolveAccountTranscripts, so the firm's own account resolves by tag rather
 * than by link — otherwise Carpenter & Company shows a handful of stray matches
 * instead of its internal meetings.
 */
function useAccountTranscripts(accountId: string) {
  return useQuery({
    queryKey: ["corpus-account-transcripts", accountId],
    queryFn: () =>
      fetchWithAuth<{ transcripts: CorpusTranscript[] }>(
        `/api/corpus/accounts/${accountId}/transcripts?limit=10`,
      ).then((r) => r.transcripts),
    retry: false,
  });
}

function TranscriptRow({ t }: { t: CorpusTranscript }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="text-xs border rounded-md p-2 space-y-1">
      <div className="font-medium">{t.title ?? "(untitled)"}</div>
      <div className="text-muted-foreground">{t.created_at?.slice(0, 10)}</div>
      {expanded && <p className="text-muted-foreground whitespace-pre-wrap pt-1">{t.summary ?? "No summary."}</p>}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"
      >
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {expanded ? "Hide" : "Summary"}
      </button>
    </div>
  );
}

const LOOKBACK_OPTIONS = [5, 10, 25, 50];

/** Ask across this account's meetings, with a control for how far back to read. */
function AskTranscripts({ accountId, available }: { accountId: string; available: number }) {
  const [question, setQuestion] = useState("");
  const [limit, setLimit] = useState(10);
  const ask = useMutation({
    mutationFn: (q: string) =>
      fetchWithAuth<{ answer: string; meetingsUsed: number; meetings: { title: string; date: string }[] }>(
        `/api/corpus/accounts/${accountId}/ask`,
        { method: "POST", body: JSON.stringify({ question: q, limit }) },
      ),
  });

  return (
    <div className="border rounded-md p-2 space-y-2 bg-muted/30">
      <div className="flex items-center gap-1.5">
        <input
          className="flex-1 text-xs bg-background border rounded px-2 py-1"
          placeholder="Ask across these meetings…"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && question.trim()) ask.mutate(question.trim());
          }}
        />
        <select
          className="text-xs bg-background border rounded px-1 py-1"
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          title="How many of the most recent meetings to read"
        >
          {LOOKBACK_OPTIONS.map((n) => (
            <option key={n} value={n}>
              last {n}
            </option>
          ))}
        </select>
        <Button
          size="sm"
          className="h-6 px-2 text-[11px]"
          disabled={!question.trim() || ask.isPending}
          onClick={() => ask.mutate(question.trim())}
        >
          {ask.isPending ? "…" : "Ask"}
        </Button>
      </div>
      {/* Say what it actually read. "Last 25" over an account with 4 meetings
          reads 4, and a wrong assumption there changes how you weigh the answer. */}
      <p className="text-[10px] text-muted-foreground">
        Reads meeting summaries, newest first. {available} available on this account.
      </p>
      {ask.isError && <p className="text-xs text-amber-700">{(ask.error as Error).message}</p>}
      {ask.data && (
        <div className="space-y-1 pt-1 border-t">
          <p className="text-xs whitespace-pre-wrap">{ask.data.answer}</p>
          <p className="text-[10px] text-muted-foreground">
            Based on {ask.data.meetingsUsed} meeting{ask.data.meetingsUsed === 1 ? "" : "s"}
            {ask.data.meetings.length > 0 &&
              `: ${ask.data.meetings.map((m) => `${m.title} (${m.date})`).join("; ")}`}
          </p>
        </div>
      )}
    </div>
  );
}

export function ClaireTranscripts({ accountId }: { accountId: string }) {
  const available = useAccountTranscripts(accountId);
  const suggested = useCorpusLinks("suggested", accountId);

  // Say WHY it's empty. "Nothing here" that means "misconfigured" is the state
  // that wastes the most time — it looks identical to "no data". Both queries
  // are checked: an error in either one used to render as "no transcripts".
  const failure = (available.error ?? suggested.error) as Error | undefined;
  if (failure) {
    return (
      <p className="text-xs text-amber-700 py-2">Transcript corpus unavailable — {failure.message}</p>
    );
  }

  const avail = available.data ?? [];
  const sugg = suggested.data ?? [];

  if (available.isLoading || suggested.isLoading) {
    return <p className="text-xs text-muted-foreground py-2">Loading…</p>;
  }
  if (!avail.length && !sugg.length) {
    return (
      <p className="text-xs text-muted-foreground py-2">
        No transcripts matched to this client.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {avail.length > 0 && <AskTranscripts accountId={accountId} available={avail.length} />}
      {sugg.length > 0 && (
        <div className="space-y-2">
          <p className="text-[11px] text-muted-foreground">
            {sugg.length} suggested — not used by the AI assistant until confirmed. Read the summary
            before deciding; a title doesn&apos;t always describe the whole recording.
          </p>
          {sugg.map((l) => (
            <LinkRow key={l.id} link={l} reviewable />
          ))}
        </div>
      )}
      {avail.length > 0 && (
        <div className="space-y-2">
          {sugg.length > 0 && <p className="text-[11px] font-medium text-muted-foreground pt-1">Available now</p>}
          {avail.map((t) => (
            <TranscriptRow key={t.id} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Count for the SideSection header — confirmed only, so it isn't inflated by unreviewed matches. */
export function useConfirmedTranscriptCount(accountId: string): number | undefined {
  const { data } = useCorpusLinks("confirmed", accountId);
  return data?.length || undefined;
}
